import React, { FC } from 'react'

import { Skeleton } from '@sketch/components'
import BillingInfoTable from '../BillingInfoTable'

import { formatPriceWithCurrency } from '@sketch/utils'

import {
  BillingPriceFragment,
  BillingSeatsInfoFragment,
  useDraftBillingSummaryQuery,
  useGetAvailablePlansQuery,
} from '@sketch/gql-types'

// This is a valid use case to narrow down Component type
// eslint-disable-next-line no-restricted-imports
import { Currency } from '@sketch/gql-types/expansive'

import {
  isBusinessPlan,
  isTeamMonthlyPlan,
  isTeamYearlyPlan,
} from '../../utils'

interface ProjectedTotalProps {
  seats: BillingSeatsInfoFragment
  customerCurrency: Currency
  projectedCost?: BillingPriceFragment
  hasSubscription?: boolean
}

interface PreviewPlanProjectedCostProps {
  seats: BillingSeatsInfoFragment
  customerCurrency: Currency
}

const BillingRow = ({ children }: React.PropsWithChildren<{}>) => (
  <BillingInfoTable.Row>
    <BillingInfoTable.HeadingCell>Projected Total</BillingInfoTable.HeadingCell>
    <BillingInfoTable.Cell>{children}</BillingInfoTable.Cell>
  </BillingInfoTable.Row>
)

const PreviewPlanProjectedCost: FC<PreviewPlanProjectedCostProps> = ({
  seats,
  customerCurrency,
}) => {
  const { data, loading: loadingPlans } = useGetAvailablePlansQuery()

  const businessPlan = data?.availablePlans.find(isBusinessPlan)
  const monthlyPlan = data?.availablePlans.find(isTeamMonthlyPlan)
  const yearlyPlan = data?.availablePlans.find(isTeamYearlyPlan)

  const { data: draftBillingSummaryYearlyData, loading: loadingYearly } =
    useDraftBillingSummaryQuery({
      variables: {
        planId: yearlyPlan?.id || '',
        seats: seats.currentSeatsTotal,
        currency: customerCurrency,
      },
      skip: !yearlyPlan,
    })

  const { data: draftBillingSummaryMonthlyData, loading: loadingMonthly } =
    useDraftBillingSummaryQuery({
      variables: {
        planId: monthlyPlan?.id || '',
        seats: seats.currentSeatsTotal,
        currency: customerCurrency,
      },
      skip: !monthlyPlan,
    })

  const { data: draftBillingSummaryBusinessData, loading: loadingBusiness } =
    useDraftBillingSummaryQuery({
      variables: {
        planId: businessPlan?.id || '',
        seats: seats.currentSeatsTotal,
        currency: customerCurrency,
      },
      skip: !businessPlan,
    })

  const loading =
    loadingPlans || loadingMonthly || loadingYearly || loadingBusiness

  if (loading) {
    return (
      <BillingRow>
        <Skeleton width="200px" />
      </BillingRow>
    )
  }

  const billingCycleAmountYearly =
    draftBillingSummaryYearlyData?.draftBillingSummary.price
  const billingCycleAmountMonthly =
    draftBillingSummaryMonthlyData?.draftBillingSummary.price
  const billingCycleAmountBusiness =
    draftBillingSummaryBusinessData?.draftBillingSummary.price

  if (
    !billingCycleAmountYearly ||
    !billingCycleAmountMonthly ||
    !billingCycleAmountBusiness
  ) {
    return (
      <BillingRow>Something went wrong fetching the projected costs</BillingRow>
    )
  }

  return (
    <BillingRow>
      <span data-testid="draft-amount">
        <strong>
          {billingCycleAmountYearly &&
            formatPriceWithCurrency(
              billingCycleAmountYearly.invoice,
              billingCycleAmountYearly.currency
            )}
        </strong>{' '}
        yearly or{' '}
        <strong>
          {billingCycleAmountMonthly &&
            formatPriceWithCurrency(
              billingCycleAmountMonthly.invoice,
              billingCycleAmountMonthly.currency
            )}
        </strong>{' '}
        monthly for the Standard plan or{' '}
        <strong>
          {billingCycleAmountBusiness &&
            formatPriceWithCurrency(
              billingCycleAmountBusiness.invoice,
              billingCycleAmountBusiness.currency
            )}
        </strong>{' '}
        yearly for the Business plan
        <br />
        With a paid subscription you would be charged for the total number of
        Editor Seats you’re using. Total excluding tax.
      </span>
    </BillingRow>
  )
}

const ProjectedTotal = (props: ProjectedTotalProps) => {
  const { hasSubscription, projectedCost, seats, customerCurrency } = props

  if (hasSubscription && projectedCost) {
    return (
      <BillingRow>
        <div>
          <strong data-testid="projected-total-amount">
            {formatPriceWithCurrency(
              projectedCost.invoice,
              projectedCost.currency
            )}
          </strong>
          <br />
          Total excluding tax
        </div>
      </BillingRow>
    )
  }

  return (
    <PreviewPlanProjectedCost
      seats={seats}
      customerCurrency={customerCurrency}
    />
  )
}

export default ProjectedTotal
